import { AVATARS, ROLES, type RoleId, sanitizeNickname } from "@agency/shared";
import { STORAGE_KEYS } from "../config.js";
import { button, el } from "./dom.js";

export interface TitleInput {
  token: string;
  nickname: string;
  avatarId: string;
  role: string;
}

function loadToken(): string {
  const existing = localStorage.getItem(STORAGE_KEYS.token);
  if (existing) return existing;
  const token = crypto.randomUUID();
  localStorage.setItem(STORAGE_KEYS.token, token);
  return token;
}

function hex(n: number): string {
  return `#${n.toString(16).padStart(6, "0")}`;
}

export class TitleScreen {
  private readonly root: HTMLElement;
  private readonly nicknameInput: HTMLInputElement;
  private readonly errorEl: HTMLElement;
  private readonly joinBtn: HTMLButtonElement;
  private readonly statusEl: HTMLElement;
  private readonly token: string;
  private avatarId: string;
  private role: RoleId = "developer";
  private readonly avatarButtons = new Map<string, HTMLButtonElement>();

  constructor(
    parent: HTMLElement,
    private readonly onJoin: (input: TitleInput) => Promise<void>,
  ) {
    this.token = loadToken();
    this.root = el("div", "overlay title-screen hidden");

    const logo = el("div", "title-logo", "THE AGENCY");
    const tag = el("div", "title-tag", "Ship it. Drink coffee. Repeat.");

    const form = el("div", "panel title-form");

    const nickField = el("div", "field");
    nickField.appendChild(el("label", undefined, "Nickname"));
    this.nicknameInput = el("input");
    this.nicknameInput.type = "text";
    this.nicknameInput.maxLength = 16;
    this.nicknameInput.placeholder = "e.g. Matteo";
    this.nicknameInput.value = localStorage.getItem(STORAGE_KEYS.nickname) ?? "";
    nickField.appendChild(this.nicknameInput);

    const avatarField = el("div", "field");
    avatarField.appendChild(el("label", undefined, "Avatar"));
    const grid = el("div", "avatar-grid");
    const savedAvatar = localStorage.getItem(STORAGE_KEYS.avatarId) ?? AVATARS[0]!.id;
    for (const avatar of AVATARS) {
      const b = button("", "avatar-choice");
      b.title = avatar.label;
      const swatch = el("div", "swatch");
      swatch.style.background = hex(avatar.tint);
      b.appendChild(swatch);
      b.appendChild(el("div", undefined, avatar.label));
      b.addEventListener("click", () => this.selectAvatar(avatar.id));
      grid.appendChild(b);
      this.avatarButtons.set(avatar.id, b);
    }
    avatarField.appendChild(grid);

    const roleField = el("div", "field");
    roleField.appendChild(el("label", undefined, "Role"));
    const roleRow = el("div", "role-row");
    for (const role of Object.values(ROLES)) {
      const b = button(`${role.icon} ${role.label}`, role.enabled ? "role-btn active" : "role-btn");
      if (role.enabled) {
        b.addEventListener("click", () => {
          this.role = role.id;
        });
      } else {
        b.disabled = true;
        b.title = "Coming soon";
      }
      roleRow.appendChild(b);
    }
    roleField.appendChild(roleRow);

    this.errorEl = el("div", "form-error");
    this.joinBtn = button("Join the office", "btn-primary");
    this.joinBtn.addEventListener("click", () => void this.submit());
    this.statusEl = el("div", "server-line", "");

    form.append(nickField, avatarField, roleField, this.errorEl, this.joinBtn, this.statusEl);
    this.root.append(logo, tag, form);
    parent.appendChild(this.root);

    this.nicknameInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") void this.submit();
    });

    this.avatarId = savedAvatar;
    this.selectAvatar(savedAvatar);
  }

  private selectAvatar(id: string): void {
    this.avatarId = id;
    for (const [avatarId, btn] of this.avatarButtons) {
      btn.classList.toggle("selected", avatarId === id);
    }
  }

  private async submit(): Promise<void> {
    // Enter on the nickname field bypasses the disabled button; never overlap joins.
    if (this.joinBtn.disabled) return;
    const result = sanitizeNickname(this.nicknameInput.value);
    if (!result.ok) {
      this.showError("Pick a nickname of 2–16 letters, numbers, spaces, _ or -.");
      return;
    }
    this.showError("");
    this.joinBtn.disabled = true;
    this.joinBtn.textContent = "Joining…";
    localStorage.setItem(STORAGE_KEYS.nickname, result.value);
    localStorage.setItem(STORAGE_KEYS.avatarId, this.avatarId);

    try {
      await this.onJoin({
        token: this.token,
        nickname: result.value,
        avatarId: this.avatarId,
        role: this.role,
      });
    } catch (error) {
      console.error("[agency] join failed", error);
      throw error;
    } finally {
      this.joinBtn.disabled = false;
      this.joinBtn.textContent = "Join the office";
    }
  }

  showError(message: string): void {
    this.errorEl.textContent = message;
  }

  setStatus(text: string): void {
    this.statusEl.textContent = text;
  }

  show(): void {
    this.root.classList.remove("hidden");
    this.refreshStatus();
  }

  hide(): void {
    this.root.classList.add("hidden");
  }

  private async refreshStatus(): Promise<void> {
    try {
      const res = await fetch("/health");
      if (!res.ok) return;
      const data = (await res.json()) as { players: number; projectPct: number };
      this.setStatus(`${data.players} developer(s) in the office — project ${data.projectPct}%`);
    } catch {
      this.setStatus("Server status unavailable.");
    }
  }
}
